import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { OwnerType } from '../../../types/__generated__';
import { CopyContextProvider } from '../../context/CopyContext';
import { usePageMeta, usePageOwner } from '../../hooks';
import { useProduct } from '../../hooks/page/product/useProduct';
import { useCharity } from '../../hooks/page/useCharity';
import useBasePageQuery from '../../hooks/useBasePageQuery';

interface CopyProviderOnePageProps {
  children: ReactNode;
}

export function CopyProvider({ children }: CopyProviderOnePageProps) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { isPageOwner } = usePageOwner();
  const { data: basePageData } = useBasePageQuery();
  const {
    data: { ownerType },
  } = useProduct();
  const { name: charityName } = useCharity();
  const { metaTagDescription } = usePageMeta();

  function getTranslations() {
    switch (ownerType) {
      case OwnerType.Individual:
        const fundraiserName = basePageData?.page?.owner?.name ?? undefined;
        const heading = isPageOwner
          ? t('shareModal.heading.pageOwner')
          : t('shareModal.heading', {
              pageOwner: fundraiserName,
            });

        return {
          shareModal: {
            heading: fundraiserName
              ? heading
              : t('shareModal.heading.fallback'),
            description: t('shareModal.description'),
          },
          supportersList: {
            noSupportersMessage: t('fundraising.noSupportersMessage', {
              name: fundraiserName,
            }),
          },
          helmet: {
            title: t('fundraising.titleExtra', {
              name: fundraiserName,
              charityName,
            }),
            ogTitle: basePageData?.page?.title ?? '',
            description:
              metaTagDescription ??
              t('fundraising.pageDescription', {
                name: fundraiserName,
                charityName,
              }) ??
              '',
            twitterDescription:
              metaTagDescription ??
              t('fundraising.twitterDescription', {
                name: fundraiserName,
                charityName,
              }) ??
              '',
          },
        };
      case OwnerType.Organisation: {
        const heading = t('shareModal.heading', {
          pageOwner: charityName,
        });

        return {
          shareModal: {
            heading: charityName ? heading : t('shareModal.heading.fallback'),
            description: t('shareModal.description'),
          },
          supportersList: {
            noSupportersMessage: t('campaign.noSupportersMessage'),
          },
          helmet: {
            title: `${basePageData?.page?.title} - JustGiving`,
            ogTitle: `${basePageData?.page?.title} - JustGiving`,
            description:
              metaTagDescription ?? basePageData?.page?.summary ?? '',
            twitterDescription: metaTagDescription ?? '',
          },
        };
      }
      default:
        return {
          shareModal: {
            heading: t('shareModal.heading.fallback'),
            description: t('shareModal.description'),
          },
          supportersList: {
            noSupportersMessage: t('campaign.noSupportersMessage'),
          },
          helmet: {
            title: `${basePageData?.page?.title} - JustGiving`,
            ogTitle: `${basePageData?.page?.title} - JustGiving`,
            description: metaTagDescription ?? '',
            twitterDescription: metaTagDescription ?? '',
          },
        };
    }
  }

  return (
    <CopyContextProvider value={getTranslations()}>
      {children}
    </CopyContextProvider>
  );
}
